import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  TipoAccion,
  TipoAccionForm,
  TipoAccionParams,
  TipoAccionPagingResp,
} from "@/erp/controlcambios/tipo-accion/types/tipoAccion.interface";

const { get, post, put, remove, patch } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/tipos-accion`;

// listar
export const useFetchGetTiposAccion = (
  params?: TipoAccionParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["tipos-accion", ...Object.values(params ?? {})],
    queryFn: async () => getTiposAccion(params),
    enabled,
  });

export const getTiposAccion = async (
  params?: TipoAccionParams
): Promise<TipoAccionPagingResp> => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<TipoAccionPagingResp>(`${BASE}?${q}`, true)) as unknown as TipoAccionPagingResp;
};

// obtener por id
export const useFetchGetByIdTipoAccion = (id: string) =>
  useQuery({
    queryKey: ["tipo-accion", id],
    queryFn: async () => getByIdTipoAccion(id),
    refetchOnWindowFocus: false,
  });

export const getByIdTipoAccion = async (id: string) =>
  await get<TipoAccion>(`${BASE}/${id}`, true);

// crear
export const useFetchPostTipoAccion = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: TipoAccionForm) => postTipoAccion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-accion"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Tipo de acción creado correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postTipoAccion = async (data: TipoAccionForm) =>
  await post<TipoAccion>(BASE, data, true);

// actualizar
export const useFetchPutTipoAccion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putTipoAccion,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tipos-accion"] });
      queryClient.invalidateQueries({ queryKey: ["tipo-accion", variables.id] });
      setAlerta(true, "Tipo de acción actualizado correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putTipoAccion = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<TipoAccionForm, "tipoAccionId">;
}) => await put<TipoAccion>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteTipoAccion = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteTipoAccion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-accion"] });
      setAlerta(true, "Tipo de acción eliminado correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteTipoAccion = async (id: string) =>
  await remove<TipoAccion>(`${BASE}/${id}`, true);

// cambiar estado
export const useFetchPatchEstadoTipoAccion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEstadoTipoAccion,
    onSuccess: () => {
      toast.success("Estado del tipo de acción actualizado");
      queryClient.invalidateQueries({ queryKey: ["tipos-accion"] });
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const patchEstadoTipoAccion = async ({ id, estado }: { id: string; estado: boolean }) =>
  await patch(`${BASE}/${id}/estado?estado=${estado}`, undefined, true);
